import * as React from 'react';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import {
  ActionPlanStatus,
  TApiActionPlanEntity,
} from '../../types/Actionplan';
import { simpleDateFormat } from '../../Helpers';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import {
  getActionPlanStyling,
} from '../../containers/pages/actionplans/ActionPlanListPage';

interface IActionPlanDetailsProps {
  actionPlan: TApiActionPlanEntity;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      marginTop: theme.spacing(3),
    },
    table: {
      width: '100%',
    },
    title: {
      flex: '1 1 100%',
      padding: theme.spacing(1, 2),
    },
  })
);

const ActionPlanDetails: React.FC<IActionPlanDetailsProps> = ({
  actionPlan,
}): JSX.Element => {
  const classes = useStyles({});
  const { t } = useTranslation();

  return (
    <Paper className={classes.root}>
      <TableContainer>
        <Typography className={classes.title} variant='h6' id='tableTitle'>
          {t('actionplan.details')}
        </Typography>
        <Table
          className={classes.table}
          size='small'
          aria-label='a dense table'
        >
          <TableBody>
            <TableRow>
              <TableCell component='th' scope='row'>
                <Box fontWeight={'bold'}>{t('actionplan.status')}</Box>
              </TableCell>
              <TableCell align='right'>
                <Chip
                  style={{
                    backgroundColor: getActionPlanStyling(
                      actionPlan.status as ActionPlanStatus
                    ).color,
                    color: '#fff',
                    textTransform: 'uppercase',
                  }}
                  size='small'
                  label={t(actionPlan.statusLabel)}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component='th' scope='row'>
                <Box fontWeight={'bold'}>{t('actionplan.risk')}</Box>
              </TableCell>
              <TableCell align='right'>
                <Chip
                  style={{
                    backgroundColor: actionPlan.riskColor,
                    color: '#000',
                    textTransform: 'uppercase',
                  }}
                  size='small'
                  label={t(actionPlan.risk)}
                />
              </TableCell>
            </TableRow>
            {actionPlan.responsiblePerson && (
              <TableRow>
                <TableCell component='th' scope='row'>
                  <Box fontWeight={'bold'}>{t('actionplan.responsible')}</Box>
                </TableCell>
                <TableCell align='right'>
                  {actionPlan.responsiblePerson}
                </TableCell>
              </TableRow>
            )}
            {actionPlan.safetyRepresentative && (
              <TableRow>
                <TableCell component='th' scope='row'>
                  <Box fontWeight={'bold'}>{t('actionplan.amr')}</Box>
                </TableCell>
                <TableCell align='right'>
                  {actionPlan.safetyRepresentative}
                </TableCell>
              </TableRow>
            )}
            {actionPlan.endDate && (
              <TableRow>
                <TableCell component='th' scope='row'>
                  <Box fontWeight={'bold'}>{t('actionplan.endDate')}</Box>
                </TableCell>
                <TableCell align='right'>
                  {simpleDateFormat(actionPlan.endDate)}
                </TableCell>
              </TableRow>
            )}
            {actionPlan.latestActivity && (
              <TableRow>
                <TableCell component='th' scope='row'>
                  <Box fontWeight={'bold'}>{t('actionplan.latest.activity')}</Box>
                </TableCell>
                <TableCell align='right'>
                  {simpleDateFormat(actionPlan.latestActivity)}
                </TableCell>
              </TableRow>
            )}
            {actionPlan.startDate && (
              <TableRow>
                <TableCell component='th' scope='row'>
                  <Box fontWeight={'bold'}>{t('actionplan.startDate')}</Box>
                </TableCell>
                <TableCell align='right'>
                  {simpleDateFormat(actionPlan.startDate)}
                </TableCell>
              </TableRow>
            )}
            {actionPlan.completedAt && (
              <TableRow>
                <TableCell component='th' scope='row'>
                  <Box fontWeight={'bold'}>{t('actionplan.completedAt')}</Box>
                </TableCell>
                <TableCell align='right'>
                  {simpleDateFormat(actionPlan.completedAt)}
                </TableCell>
              </TableRow>
            )}
            {actionPlan.revisedAt && (
              <TableRow>
                <TableCell component='th' scope='row'>
                  <Box fontWeight={'bold'}>{t('actionplan.revisedAt')}</Box>
                </TableCell>
                <TableCell align='right'>
                  {simpleDateFormat(actionPlan.revisedAt)}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ActionPlanDetails;
